import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Drawer, 
  Table, 
  Button, 
  Space, 
  Input, 
  Typography,
  Radio,
  Switch,
  message,
  theme,
  Popconfirm,
  Badge,
  Flex,
  Splitter
} from 'antd';
import { 
  CopyOutlined,
  CheckOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { RefreshIcon } from '../common/ActionIcons';
import { CiSearch } from "react-icons/ci";
import { FiEdit,  FiX, FiCheck } from 'react-icons/fi';
import { IoLocationSharp } from "react-icons/io5";
import { useTranslation } from 'react-i18next';
import dayjs from '../../utils/dayjs';
import debounce from 'lodash/debounce';

import { 
  listLocationsByUploadBatch, 
  listLocations,
  updateLocation, 
  loadLocationDto 
} from '../../actions/locationActions';

import { getUploadBatchDetails } from '../../actions/uploadBatchActions';
import SubjectsByLocationExplorer from './SubjectsByLocationExplorer.component';

const { Text, Title } = Typography;

// Desc Component
const Desc = props => (
  <Flex justify="center" align="center" style={{ height: '100%' }}>
    <Typography.Title type="secondary" level={5} style={{ whiteSpace: 'nowrap' }}>
      {props.text}
    </Typography.Title>
  </Flex>
);

// Component 1: Simplified Location List
const LocationList = ({ 
  selectedLocationId, 
  onLocationSelect, 
  uploadBatchId,
  filterByUploadBatch,
  t 
}) => {
  const dispatch = useDispatch();
  const { token } = theme.useToken();
  
  // Redux state - use different states based on filter
  const locationUploadBatch = useSelector(state => state.locationUploadBatch);
  const locationAll = useSelector(state => state.locationList);
  
  const locationList = filterByUploadBatch ? locationUploadBatch : locationAll;
  const { loading, locations = [], error, currentPage, totalPages, totalCount } = locationList;

  const locationUpdate = useSelector(state => state.locationUpdate);
  const { loading: loadingUpdate, success: successUpdate, error: errorUpdate } = locationUpdate;

  // Simplified filter state - following EntityManagementScreen pattern
  const [limit, setLimit] = useState(20);
  const [listFilter, setListFilter] = useState({
    query: "",
    page: 1,
    limit: limit,
    filter: "all"
  });

  const [searchText, setSearchText] = useState('');
  const [updatingLocationId, setUpdatingLocationId] = useState(null);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [editingName, setEditingName] = useState('');

  // Load locations on component mount
  useEffect(() => {
    if (uploadBatchId) {
      dispatch(loadLocationDto());
      if (filterByUploadBatch) {
        dispatch(listLocationsByUploadBatch(uploadBatchId, listFilter));
      } else {
        dispatch(listLocations(listFilter));
      }
    }
  }, [dispatch, uploadBatchId, filterByUploadBatch]);

  // Handle filter changes
  useEffect(() => {
    if (uploadBatchId && listFilter) {
      if (filterByUploadBatch) {
        dispatch(listLocationsByUploadBatch(uploadBatchId, listFilter));
      } else {
        dispatch(listLocations(listFilter));
      }
    }
  }, [listFilter, uploadBatchId, dispatch, filterByUploadBatch]);

  // Handle update success
  useEffect(() => {
    if (successUpdate) {
      message.success(t('Location updated successfully'));
      setUpdatingLocationId(null);
      setEditingLocationId(null);
      setEditingName('');
      dispatch({ type: 'LOCATION_UPDATE_RESET' });
    }
  }, [successUpdate, t, dispatch]);

  // Handle errors
  useEffect(() => {
    if (errorUpdate) {
      message.error(Array.isArray(errorUpdate) ? errorUpdate.join(', ') : errorUpdate);
      setUpdatingLocationId(null);
      setEditingLocationId(null);
      setEditingName('');
      dispatch({ type: 'LOCATION_UPDATE_RESET' });
    }
  }, [errorUpdate, t, dispatch]);

  // Simplified filter change handler - following EntityManagementScreen pattern
  const handleFilterChange = useCallback((newFilter) => {
    setListFilter(prevFilter => ({
      ...prevFilter,
      ...newFilter
    }));
  }, []);

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((value) => {
      handleFilterChange({ query: value, filter: "name", page: 1 });
    }, 300),
    [handleFilterChange]
  );

  const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  const handleLocationClick = useCallback((record) => {
    onLocationSelect(record.id);
  }, [onLocationSelect]);

  const handleToggleApprovalScope = useCallback((record) => {
    const newApprovalStatus = !record.isInApprovedScope;
    setUpdatingLocationId(record.id);
    dispatch(updateLocation(record.id, { isInApprovedScope: newApprovalStatus }));
  }, [dispatch]);

  const handlePageChange = useCallback((page) => {
    handleFilterChange({ page });
  }, [handleFilterChange]);

  const handleStartEdit = useCallback((record, e) => {
    e.stopPropagation();
    setEditingLocationId(record.id);
    setEditingName(record.name);
  }, []);

  const handleSaveEdit = useCallback((record, e) => {
    e.stopPropagation();
    if (editingName.trim() !== record.name) {
      setUpdatingLocationId(record.id);
      dispatch(updateLocation(record.id, { name: editingName.trim() }));
    } else {
      setEditingLocationId(null);
      setEditingName('');
    }
  }, [dispatch, editingName]);

  const handleCancelEdit = useCallback((e) => {
    e.stopPropagation();
    setEditingLocationId(null);
    setEditingName('');
  }, []);

  const handleFilterTypeChange = useCallback((e) => {
    const [filterType, queryValue] = e.target.value.split('-');
    handleFilterChange({ 
      filter: filterType, 
      query: queryValue, 
      page: 1 
    });
  }, [handleFilterChange]);

  const handleRefresh = useCallback(() => {
    if (uploadBatchId) {
      if (filterByUploadBatch) {
        dispatch(listLocationsByUploadBatch(uploadBatchId, listFilter));
      } else {
        dispatch(listLocations(listFilter));
      }
    }
  }, [uploadBatchId, listFilter, dispatch, filterByUploadBatch]);

  const exportToCSV = useCallback(() => {
    if (!locations || locations.length === 0) {
      message.warning(t('No locations to export'));
      return;
    }

    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += 'Location Name,Postcode,ODS Code,Location Code,Approval Status\n';
    
    locations.forEach((location) => {
      const name = `"${(location.name || '').replace(/"/g, '""')}"`;
      const postcode = location.postcode || '';
      const odsCode = location.odscode || '';
      const locationCode = location.locationCode || '';
      const approvalStatus = location.isInApprovedScope ? t('Approved') : t('Not Approved');
      csvContent += `${name},${postcode},${odsCode},${locationCode},${approvalStatus}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `locations_${dayjs().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success(t('CSV exported successfully'));
  }, [locations, t]);

  // Table columns
  const columns = [
    {
      title: t('Location'),
      key: 'location',
      render: (_, record) => {
        const isSelected = selectedLocationId === record.id;
        const isUpdating = updatingLocationId === record.id;
        
        return (
          <div 
            style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              cursor: 'pointer',
              backgroundColor: isSelected 
                ? token.colorPrimaryBg 
                : record.isInApprovedScope 
                  ? `${token.colorSuccessBg}20`
                  : `${token.colorWarningBg}20`,
              borderRadius: token.borderRadius,
              border: isSelected ? `1px solid ${token.colorPrimary}` : '1px solid transparent',
              transition: 'background-color 0.2s ease',
              marginBottom: '4px'
            }}
            onClick={() => handleLocationClick(record)}
            className="location-row"
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                fontSize: '14px', 
                fontWeight: '500', 
                display: 'flex', 
                alignItems: 'center',
                marginBottom: '6px',
                color: token.colorText,
                gap: '8px'
              }}>
                <IoLocationSharp 
                  style={{ 
                    color: record.isInApprovedScope ? token.colorSuccess : token.colorWarning,
                    fontSize: '16px'
                  }} 
                />
                {editingLocationId === record.id ? (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    flex: 1,
                    minWidth: 0
                  }}>
                    <Input
                      size="middle"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onPressEnter={(e) => handleSaveEdit(record, e)}
                      onBlur={(e) => handleSaveEdit(record, e)}
                      autoFocus
                      style={{ 
                        fontSize: '14px',
                        fontWeight: '500',
                        flex: 1,
                        minWidth: 0
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                      type="primary"
                      size="small"
                      icon={<FiCheck size={14} />}
                      onClick={(e) => handleSaveEdit(record, e)}
                      style={{ 
                        padding: '4px 8px',
                        minWidth: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    />
                    <Button
                      type="default"
                      size="small"
                      icon={<FiX size={14} />}
                      onClick={handleCancelEdit}
                      style={{ 
                        padding: '4px 8px',
                        minWidth: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    flex: 1,
                    overflow: 'hidden'
                  }}>
                    <span style={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1
                    }}>
                      {record.name}
                    </span>
                    <Button
                      type="text"
                      size="small"
                      icon={<FiEdit size={14} />}
                      onClick={(e) => handleStartEdit(record, e)}
                      style={{ 
                        color: token.colorTextTertiary,
                        padding: '4px 6px',
                        minWidth: '28px',
                        height: '28px',
                        opacity: 0.6,
                        transition: 'opacity 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      className="edit-btn"
                    />
                  </div>
                )}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: token.colorTextSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{record.postcode}</span>
                <span>•</span>
                <span style={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {record.odscode || record.locationCode || 'No ODS Code'}
                </span>
              </div>
            </div>
            
            <div style={{ marginLeft: 12, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Popconfirm
                title={
                  record.isInApprovedScope 
                    ? t('Remove from approved scope?')
                    : t('Add to approved scope?')
                }
                description={
                  record.isInApprovedScope
                    ? t('This location will no longer be in the approved scope.')
                    : t('This location will be added to the approved scope.')
                }
                onConfirm={(e) => {
                  e?.stopPropagation();
                  handleToggleApprovalScope(record);
                }}
                onCancel={(e) => e?.stopPropagation()}
                okText={t('Yes')}
                cancelText={t('Cancel')}
                okButtonProps={{
                  type: record.isInApprovedScope ? 'default' : 'primary',
                  danger: record.isInApprovedScope
                }}
                placement="topRight"
              >
                <Button
                  type={record.isInApprovedScope ? 'default' : 'primary'}
                  danger={record.isInApprovedScope}
                  size="small"
                  loading={isUpdating}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    borderRadius: token.borderRadius,
                    fontWeight: '500',
                    padding: '4px 12px',
                    height: 'auto',
                    minWidth: '80px'
                  }}
                >
                  {record.isInApprovedScope ? t('Disapprove') : t('Approve')}
                </Button>
              </Popconfirm>
            </div>
          </div>
        );
      }
    }
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '16px',
      }}
    >
      {/* Header Section */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Title level={4} style={{ margin: 0, color: token.colorText }}>
              {t('Locations')}
            </Title>
            <Badge 
              count={locations.length} 
              style={{ backgroundColor: token.colorPrimary }}
              showZero
            />
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <Radio.Group
              value={`${listFilter.filter}-${listFilter.query}`}
              onChange={handleFilterTypeChange}
              buttonStyle="solid"
              size="small"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px'
              }}
            >
              <Radio.Button value="all-" style={{ flex: '1 1 auto', minWidth: '60px', textAlign: 'center' }}>
                {t('All')}
              </Radio.Button>
              <Radio.Button value="isInApprovedScope-true" style={{ flex: '1 1 auto', minWidth: '80px', textAlign: 'center' }}>
                {t('Approved')}
              </Radio.Button>
              <Radio.Button value="isInApprovedScope-false" style={{ flex: '1 1 auto', minWidth: '80px', textAlign: 'center' }}>
                {t('Not Approved')}
              </Radio.Button>
            </Radio.Group>
            <Button
              type="text"
              icon={<RefreshIcon />}
              onClick={handleRefresh}
              title={t('Refresh')}
              style={{ 
                borderRadius: token.borderRadius,
                color: token.colorTextSecondary
              }}
            />
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={exportToCSV}
              title={t('Export to CSV')}
              style={{ 
                borderRadius: token.borderRadius,
                color: token.colorTextSecondary
              }}
            />
          </div>
        </div>

        {/* Search Input */}
        <Input
          size="large"
          placeholder={t('Search locations by name, postcode, city, or codes')}
          prefix={<CiSearch size={18} style={{ color: token.colorTextSecondary }} />}
          value={searchText}
          onChange={handleSearch}
          allowClear
          style={{ 
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorder}`,
            boxShadow: 'none'
          }}
        />
      </div>

      {/* Table Section */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Table
          size="small"
          loading={loading || loadingUpdate}
          dataSource={locations}
          columns={columns}
          rowKey="id"
          showHeader={false}
          pagination={{
            current: currentPage || 1,
            pageSize: listFilter.limit,
            total: totalCount || 0,
            onChange: handlePageChange,
            size: 'small',
            style: { 
              textAlign: 'center', 
              margin: '12px 0 0 0',
              padding: '8px 0'
            },
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} ${t('of')} ${total} ${t('locations')}`
          }}
          scroll={{ y: 'calc(100vh - 400px)' }}
          locale={{ 
            emptyText: error 
              ? t('Error loading locations') 
              : t('No locations found') 
          }}
          style={{
            '.ant-table-tbody > tr > td': {
              border: 'none',
              padding: '4px 0'
            }
          }}
        />
      </div>

      <style jsx>{`
        .location-row:hover {
          background-color: ${token.colorBgTextHover} !important;
        }
        .location-row:hover .edit-btn {
          opacity: 1 !important;
        }
        
        /* Responsive styles for radio buttons */
        @media (max-width: 768px) {
          .ant-radio-group {
            width: 100%;
            justify-content: center;
          }
          .ant-radio-button-wrapper {
            flex: 1 1 auto !important;
            min-width: 60px !important;
            text-align: center !important;
          }
        }
        
        @media (max-width: 480px) {
          .ant-radio-button-wrapper {
            min-width: 50px !important;
            font-size: 12px !important;
            padding: 4px 8px !important;
          }
        }
      `}</style>
    </div>
  );
};

// Component 2: Subject List for Selected Location
const LocationSubjectList = ({ 
  selectedLocationId, 
  t,
  filterByUploadBatch,
  uploadBatchId
}) => {
  return (
    <SubjectsByLocationExplorer 
      selectedLocationId={selectedLocationId}
      t={t}
      filterByUploadBatch={filterByUploadBatch}
      uploadBatchId={uploadBatchId}
    />
  );
};

// Main LocationExplorer Component
const LocationExplorer = ({ 
  visible, 
  onClose,
  uploadBatchId 
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const dispatch = useDispatch();
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [filterByUploadBatch, setFilterByUploadBatch] = useState(true); // Default to true
  const [copiedId, setCopiedId] = useState(false);
  
  // Get locations data for statistics
  const locationList = useSelector(state => state.locationUploadBatch);
  const { locations = [] } = locationList;
  
  // Get upload batch details
  const uploadBatchDetails = useSelector(state => state.uploadBatchDetails);
  const { uploadBatch, loading: loadingUploadBatch } = uploadBatchDetails;

  const handleLocationSelect = useCallback((locationId) => {
    setSelectedLocationId(locationId);
  }, []);

  // Load upload batch details when component becomes visible
  useEffect(() => {
    if (visible && uploadBatchId) {
      dispatch(getUploadBatchDetails(uploadBatchId));
    }
  }, [visible, uploadBatchId, dispatch]);

  const handleClose = useCallback(() => {
    setSelectedLocationId(null);
    onClose();
  }, [onClose]);

  const handleCopyId = useCallback(() => {
    if (uploadBatch?.id) {
      navigator.clipboard.writeText(uploadBatch.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 3000);
    }
  }, [uploadBatch?.id]);

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Space size="middle">
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: token.colorPrimaryBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <IoLocationSharp 
                style={{ 
                  fontSize: '20px', 
                  color: token.colorPrimary 
                }} 
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Title level={4} style={{ margin: 0, color: token.colorText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {loadingUploadBatch ? t('Loading...') : t('Location Explorer') + ' - ' + (uploadBatch?.fileName || t('No file selected'))}
              </Title>
              <Text type="secondary" style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {uploadBatch && (
                  <>
                    <span>{t('Total Records')}: {uploadBatch.totalRecords || 0}</span>
                    <span>•</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {t('UploadBatch')}: {uploadBatch.id || t('No id')}
                      {uploadBatch.id && (
                        <Button
                          type="text"
                          size="small"
                          icon={copiedId ? <CheckOutlined /> : <CopyOutlined />}
                          onClick={handleCopyId}
                          style={{
                            padding: '2px 4px',
                            height: 'auto',
                            minWidth: '20px',
                            color: copiedId ? token.colorSuccess : token.colorTextSecondary
                          }}
                          title={copiedId ? t('Copied!') : t('Copy UploadBatch ID')}
                        />
                      )}
                    </span>
                    <span>•</span>
                    <span>{t('Date')}: {dayjs(uploadBatch.uploadDateTime).format('DD/MM/YYYY HH:mm')}</span>
                    <span>•</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Switch
                        size="small"
                        checked={filterByUploadBatch}
                        onChange={setFilterByUploadBatch}
                        style={{ marginRight: '4px' }}
                      />
                      <span style={{ fontSize: '11px', color: token.colorTextTertiary }}>
                        {filterByUploadBatch ? t('Upload Batch Mode') : t('All Locations Mode')}
                      </span>
                    </span>
                  </>
                )}
                {!uploadBatch && t('Manage location approval status')}
              </Text>
            </div>
          </Space>
        </div>
      }
      placement="bottom"
      size="large"
      onClose={handleClose}
      open={visible}
     
      styles={{
        body: { 
          padding: '16px',
          backgroundColor: token.colorBgLayout 
        },
        header: { 
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          padding: '16px 24px',
          backgroundColor: token.colorBgContainer
        }
      }}
    >
      <Splitter 
        style={{ 
          height: 'calc(100vh - 140px)',
          borderRadius: '6px'
        }}
      >
        <Splitter.Panel 
          defaultSize="25%" 
          min="20%" 
          max="40%"
          style={{ overflow: 'hidden' }}
        >
          <LocationList 
            selectedLocationId={selectedLocationId}
            onLocationSelect={handleLocationSelect}
            uploadBatchId={uploadBatchId}
            filterByUploadBatch={filterByUploadBatch}
            t={t}
          />
        </Splitter.Panel>
        <Splitter.Panel style={{ overflow: 'hidden', padding: '16px' }}>
          {selectedLocationId ? (
            <LocationSubjectList 
              selectedLocationId={selectedLocationId}
              t={t}
              filterByUploadBatch={filterByUploadBatch}
              uploadBatchId={uploadBatchId}
            />
          ) : (
            <Desc text={t('Select a location to view subjects')} />
          )}
        </Splitter.Panel>
      </Splitter>
    </Drawer>
  );
};

export default LocationExplorer;